import { inject, Injectable } from '@angular/core';
import { PermissionsApiService, GetPermissionsRequestInterface } from '@vendasta/platform-users';
import { CrmInjectionToken } from '../tokens-and-interfaces';
import { combineLatest, switchMap, of, map, shareReplay, Observable } from 'rxjs';
import { FeatureFlagsApiService } from '@vendasta/partner';
import { IAMService, UserIdentifier } from '@vendasta/iamv2';
import { catchError } from 'rxjs/operators';
import { User } from '@vendasta/iamv2/lib/user/user';

const PartnerCenterClientAppID = 'partner-center-client';
const ShellClientAppID = 'shell-client';
const AppsRestrictedByMarkets = [PartnerCenterClientAppID, ShellClientAppID];

@Injectable()
export class RestrictedGroupService {
  private readonly config = inject(CrmInjectionToken);
  private readonly permissionsApiService = inject(PermissionsApiService);
  private readonly featureFlagService = inject(FeatureFlagsApiService);
  private readonly iamService = inject(IAMService);

  private featureEnabled$ = this.config.namespace$.pipe(
    switchMap((namespace: string) => {
      const isNotSupported = !AppsRestrictedByMarkets.includes(this.config.appID || '');
      if (isNotSupported) {
        return of({
          hasAccess: [true],
        });
      }

      return this.featureFlagService.batchGetStatus({
        featureIds: ['crm_market_restriction'],
        partnerId: namespace || '',
      });
    }),
    map((response) => {
      return response?.hasAccess.length > 0 && response?.hasAccess[0];
    }),
  );
  private currentUser$ = this.config.currentUserId$.pipe(
    switchMap((userId) => {
      const uniqueUserIdentifiers = [new UserIdentifier({ userId: userId })];

      return this.iamService.getMultiUsers(uniqueUserIdentifiers);
    }),
    map((response) => response && response[0]),
    catchError(() => {
      return of(null);
    }),
  );
  private currentGroupId$ = combineLatest([
    this.config.currentUserId$,
    this.currentUser$,
    this.config.namespace$,
    this.featureEnabled$,
  ]).pipe(
    switchMap(([userId, user, namespace, featureEnabled]: [string, User | null, string, boolean]) => {
      const roles = user?.roles;
      const hasDeveloperRole = 'developer' in roles;
      const isSuperAdmin = roles['partner']?.attributes?.attributes['is_super_admin']?.boolAttribute === true;
      const isNotSupported = !AppsRestrictedByMarkets.includes(this.config.appID || '');
      if (!featureEnabled || isNotSupported || hasDeveloperRole || isSuperAdmin) {
        return of('');
      }

      return this.permissionsApiService.getPermissions({
        partnerId: namespace,
        userId: userId,
      } as GetPermissionsRequestInterface);
    }),
    map((response) => {
      if (typeof response === 'string') {
        return '';
      }
      return response?.permissions?.restrictedToMarketId ?? '';
    }),
    shareReplay(1),
  );

  getCurrentGroupId(): Observable<string> {
    return this.currentGroupId$;
  }
}
